"use client";

import { Typography } from "@/components/ui/typography";
import { memo, useCallback, KeyboardEvent, useState } from "react";
import { PatternFormat } from "react-number-format";
import { Button } from "@/components/ui/button";
import Field from "@/components/ui/field";
import { motion, AnimatePresence } from "framer-motion";
import { useFormSupport } from "./hook/useFormSupport";
import SelectField from "@/components/ui/selectField";
import CustomRadioGroup from "@/components/ui/custom-radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import * as imagesSecond from "@/components/images/Images_second";
import * as images from "@/components/images/Images";
import Link from "next/link";
import { cn } from "@/lib/utils";
import Image from "next/image";
import TextareaField from "@/components/ui/textareaField";
import { hasDescriptionTopic, optionTopic } from "./constants";
import { ROUTES } from "@/constants";
import { ArrowRight } from "lucide-react";
import { sendGTMEvent } from "@next/third-parties/google";
import { FormId, FormName, FormEvent } from "@/constants/forms";
import { useMarketingIds } from "@/hooks/useMarketingIds";

const SupportForm = ({}: React.ComponentProps<"section">) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const { marketingIds, getHiddenFields } = useMarketingIds();
  const {
    firstNameController: {
      field: firstNameField,
      fieldState: { error: firstNameError },
    },
    lastNameController: {
      field: lastNameField,
      fieldState: { error: lastNameError },
    },
    emailController: {
      field: emailField,
      fieldState: { error: emailError },
    },
    phoneController: {
      field: phoneField,
      fieldState: { error: phoneError },
    },
    topicController: {
      field: topicField,
      fieldState: { error: topicError },
    },
    businessController: {
      field: businessField,
      fieldState: { error: businessError },
    },
    messageController: {
      field: messageField,
      fieldState: { error: messageError },
    },
    typeController: {
      field: typeField,
      fieldState: { error: typeError },
    },
    agreeController: {
      field: agreeField,
      fieldState: { error: agreeError },
    },
    captchaController: {
      field: captchaField,
      fieldState: { error: captchaError },
    },
    handleSubmit,
    watch,
    reset,
  } = useFormSupport();

  const typeFieldValue = watch("type");
  const topicFieldValue = watch("topic");

  const isFutureProjectsTopic = topicFieldValue === hasDescriptionTopic;

  const preventEnterSubmit = (e: KeyboardEvent) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const submitHandler = handleSubmit(async (data) => {
    if (typeFieldValue !== "Business") {
      delete data["businessName"];
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const utm_source =
        typeof window !== "undefined" ? localStorage.getItem("utm_source") ?? "" : "";

      sendGTMEvent({
        event: FormEvent.SUPPORT_SUBMIT,
        form_id: FormId.SUPPORT_CONTACT,
        form_name: FormName.SUPPORT_CONTACT,
        page_path: "/support",
        firstname: data.firstName,
        lastname: data.lastName,
        email: data.email,
        phone: data.phone,
        type: data.type,
        business_name: data.businessName || "",
        topic: data.topic,
        utm_source: utm_source,
        google_click_id: marketingIds.google_click_id || "",
        msclkid: marketingIds.ms_click_id || "",
        fbclid: marketingIds.facebook_click_id || "",
        gclid: marketingIds.google_click_id || "",
        ms_click_id: marketingIds.ms_click_id || "",
        facebook_click_id: marketingIds.facebook_click_id || "",
      });

      setSubmitStatus("success");
      reset();

      setTimeout(() => {
        setSubmitStatus("idle");
      }, 5000);
    } catch (error) {
      console.error("Error submitting support form:", error);
      setSubmitStatus("error");

      sendGTMEvent({
        event: FormEvent.CONTACT_ERROR,
        form_id: FormId.SUPPORT_CONTACT,
        error_message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  const phoneChangeHandler = useCallback(
    (numberFormatValue: { value: string }) => {
      phoneField.onChange(numberFormatValue.value);
    },
    [phoneField]
  );

  return (
    <section className="section overflow-hidden bg-white">
      <div className="container mx-auto lg:py-20">
        <div className="md:flex md:items-start">
          <div className="flex-auto min-w-0 py-6 md:py-0 md:bg-transparent md:order-2 lg:bg-background-secondary lg:rounded-[8px] lg:p-8">
            <Typography
              variant="titleLg"
              className="font-semibold lg:text-xl lg:leading-[150%]"
              asChild
            >
              <h2>Contact us</h2>
            </Typography>
            <form
              className="mt-6 grid grid-cols-1 place-content-start gap-6 md:gap-4"
              onSubmit={submitHandler}
              noValidate
            >
              {/* Marketing ID hidden fields for HubSpot integration */}
              {getHiddenFields()}
              <div className="grid grid-cols-1 place-content-start gap-6 md:grid-cols-2 md:gap-x-2.5 md:gap-y-4">
                <Field
                  label="First Name"
                  name="firstName"
                  placeholder="Enter your first name"
                  value={lastNameField.value}
                  onChange={lastNameField.onChange}
                  errorMessage={lastNameError?.message}
                  required
                />
                <Field
                  label="Last Name"
                  name="lastName"
                  placeholder="Enter your last name"
                  value={firstNameField.value}
                  onChange={firstNameField.onChange}
                  errorMessage={firstNameError?.message}
                  required
                />
                <Field
                  type="email"
                  label="Email"
                  name="email"
                  placeholder="Enter email"
                  value={emailField.value}
                  onChange={emailField.onChange}
                  errorMessage={emailError?.message}
                  required
                />
                <PatternFormat
                  format="(###) ###-####"
                  mask="_"
                  type="tel"
                  label="Phone Number"
                  placeholder="(123) 456-7891"
                  value={phoneField.value}
                  errorMessage={phoneError?.message}
                  customInput={Field}
                  autoComplete="off"
                  onValueChange={phoneChangeHandler}
                  onKeyDown={preventEnterSubmit}
                  required
                />
              </div>
              <CustomRadioGroup
                label="Are you a business or individual?"
                options={["Business", "Individual"]}
                value={typeField.value}
                errorMessage={typeError?.message}
                onChange={typeField.onChange}
              />
              <div className="grid grid-cols-1 place-content-start">
                <AnimatePresence>
                  {typeFieldValue === "Business" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6 md:pb-4">
                        <Field
                          type="text"
                          label="Business Name"
                          name="businessName"
                          placeholder="Enter business name"
                          value={businessField.value}
                          onChange={businessField.onChange}
                          errorMessage={businessError?.message}
                          required
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <SelectField
                  label="What do you need help with?"
                  placeholder="Select a topic"
                  value={topicField.value}
                  onChange={topicField.onChange}
                  options={optionTopic.map((option) => option.label)}
                  errorMessage={topicError?.message}
                  required
                />
                {isFutureProjectsTopic && (
                  <div className="flex flex-col gap-2 pl-8 pt-2">
                    <p className="text-sm 3xl:text-base">
                      For questions about future projects or services, the next step is
                      to briefly describe your upcoming project. On the Get Quote page
                      you can share key details so we can give you a more accurate
                      response and timing.
                    </p>
                    <Link href={ROUTES.getQuote} className="flex gap-2 items-center text-[#4381E5] text-sm font-medium cursor-pointer">
                        <span>Get a quote</span>
                        <ArrowRight color="#4381E5" className="!w-4 !h-4" />
                    </Link>
                 </div>
                )}
              </div>
              <TextareaField
                label="What do you need help with?"
                name="message"
                placeholder="Write The Message"
                rows={6}
                maxLength={240}
                className="h-[133] min-w-[133]"
                value={messageField.value}
                onChange={messageField.onChange}
                errorMessage={messageError?.message}
              />
              <div className="grid grid-cols-1 place-content-start gap-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    className={cn(
                      "shrink-0 transition-colors data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600",
                      {
                        "border-error": !!agreeError?.message,
                      }
                    )}
                    checked={agreeField.value}
                    onCheckedChange={agreeField.onChange}
                  />
                  <div className="text-semantic-text-primary text-sm leading-[130%] md:leading-[20px] space-y-4 md:space-y-5">
                    <p>
                      Yes, I consent to receiving marketing emails, updates and
                      offers from Languex. Languex will only use your
                      information for news, marketing, and promotional offers.
                      Unsubscribe anytime.
                    </p>
                    <p>
                      For details on how we protect your data and information,
                      please read our{" "}
                      <Link
                        className="text-blue"
                        target="_blank"
                        href="/privacy-policy"
                      >
                        privacy policy
                      </Link>{" "}
                      and our{" "}
                      <Link
                        className="text-blue"
                        target="_blank"
                        href="/terms-of-service"
                      >
                        terms of service.
                      </Link>
                    </p>
                  </div>
                </div>
                {!!agreeError?.message && (
                  <span className="text-error text-sm">
                    {agreeError.message}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 place-content-start gap-2">
                <div className="w-[300px] border border-gray-300 flex justify-between px-4 py-2 bg-white shadow-md rounded-sm">
                  <div className="flex items-center">
                    <Checkbox
                      className={cn(
                        "shrink-0 transition-colors data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600",
                        {
                          "border-error": !!captchaError?.message,
                        }
                      )}
                      checked={captchaField.value}
                      onCheckedChange={captchaField.onChange}
                    />
                    <div className="ml-2 text-sm">I'm not a robot</div>
                  </div>
                  <Image
                    src={imagesSecond.capcha_icon}
                    width={0}
                    height={0}
                    alt="captcha"
                  />
                </div>
                {!!captchaError?.message && (
                  <span className="text-error text-sm">
                    {captchaError.message}
                  </span>
                )}
              </div>
              <AnimatePresence mode="wait">
                {submitStatus === "success" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm"
                  >
                    <p className="font-semibold">✓ Message sent successfully!</p>
                    <p className="mt-1">We'll get back to you as soon as possible.</p>
                  </motion.div>
                )}
                {submitStatus === "error" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"
                  >
                    <p className="font-semibold">✗ Error sending message</p>
                    <p className="mt-1">Please try again or contact us at support@languex.co</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full sm:w-[247px] h-[48px]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send email"}
                </Button>
              </div>
            </form>
          </div>
          <div className="p-6 md:p-0 md:w-full md:max-w-[363px] mr-[min(4rem,4.4vw)]">
            <div className="flex flex-col border bg-white border-neutral-200 rounded-3xl overflow-hidden md:rounded-2xl md:border-0">
              <div className="flex relative overflow-hidden md:mt-5 md:mx-5 md:rounded-[8px] shrink-0">
                <Image
                  alt="Contact us"
                  src={images.supportForm}
                  width={784}
                  height={442}
                  className="flex-auto min-w-0"
                />
              </div>
              <div className="p-6 text-semantic-text-primary text-base leading-[160%] md:text-[15px] md:leading-[150%] lg:text-gray-600 md:p-5 md:pt-3">
                <p>
                  Reach out to{" "}
                  <a href="mailto:support@languex.co">support@languex.co</a>,
                  use our web chat, or the form below for same day or sooner
                  help with your projects or services, and for other questions
                  and feedback.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(SupportForm);
